import styled from '@emotion/styled';
import { MessageExtendComplex } from '../MessageExtend/MessageExtendComplex';
import { BaseMsgProps } from './types';
import { ImageMessage } from 'components/Dialogue';
import ExtraMenu, { Placement } from '../MessageMenu/ExtraMenu';
import { useChatContext } from '../../contexts/ChatContext';
import { useMemo } from 'react';
import { getMetaFromSender } from '../../utils';
import { useGetTranslation } from './TextMsg';
import { getFacebookMessage } from 'services/api/conversation';
import { useUpdateMessage } from 'hooks/business/message';
import { noop } from 'lodash-es';

const FacebookImageMsg = (props: BaseMsgProps) => {
  const { raw, msg, conversation } = props;
  const {
    isGalaxy,
    dialogRef,
    singleTransToSysLang,
    showFeedback,
    setContextMenuMessage,
    showContextMenu,
  } = useChatContext();
  const { sysLang } = useGetTranslation();

  const updateMessage = useUpdateMessage();

  const { channel } = conversation;
  const { sender } = msg;
  const { fbMsgContent: { expireTimestamp, attachments: { data = [] } = {} } = {} } = raw;
  const imgData = data.filter(({ imageData }) => imageData?.url);

  const getMsg = (index: number): typeof msg => {
    const {
      imageData: { url, width, height },
    } = imgData[index];
    return {
      ...msg,
      type: 'image',
      content: {
        url,
      },
      raw: {
        ...raw,
        messageType: 'IMAGE',
        imageContent: {
          imgUrl: url,
          externalImgUrl: url,
          width,
          height,
        },
      },
    };
  };

  const { layout } = useMemo(() => getMetaFromSender(sender), [sender]);
  const extraMenuRender = (placement: Placement) => (
    <ExtraMenu
      uiMessage={getMsg(0)}
      conversation={conversation}
      isGalaxy={isGalaxy}
      onTranslate={singleTransToSysLang}
      onFeedback={showFeedback}
      channel={channel}
      targetLanguage={sysLang}
      placement={placement}
    />
  );

  if (!imgData.length) {
    return <></>;
  }

  return (
    <MessageExtendComplex
      {...props}
      content={
        <MenuContainer>
          {layout === 'right' && extraMenuRender('left')}
          <CombineWrapper>
            {imgData.map(({ imageData: { url, width, height } }, index) => {
              return (
                <MenuContent
                  layout="left"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenuMessage(getMsg(index));
                    setTimeout(() => {
                      showContextMenu(e);
                    });
                  }}
                >
                  <ImageMessage
                    src={url}
                    width={imgData.length === 1 ? 200 : imgData.length === 2 ? 150 : 100}
                    imgSize={{ width, height }}
                    lazyScrollContainer={dialogRef?.dom}
                    onLoad={
                      expireTimestamp &&
                      Number(expireTimestamp) > 0 &&
                      Date.now() > Number(expireTimestamp)
                        ? () => {
                            getFacebookMessage({ storeId: raw.storeId, msgId: raw.msgId })
                              .then(({ fbMsgContent }) => {
                                updateMessage(raw.msgId, (prev) => {
                                  if (!prev) {
                                    return;
                                  }
                                  return {
                                    ...prev,
                                    fbMsgContent,
                                  };
                                });
                              })
                              .catch(noop);
                          }
                        : undefined
                    }
                  />
                </MenuContent>
              );
            })}
          </CombineWrapper>
          {layout === 'left' && extraMenuRender('right')}
        </MenuContainer>
      }
    />
  );
};

const MenuContainer = styled.div`
  display: flex;
  align-items: flex-end;
  > div {
    flex: 1 0 0;
  }

  .menu-content {
    display: flex;
    flex-direction: column;
  }
  .menu-content-left {
    align-items: start;
  }
  .menu-content-right {
    align-items: end;
  }
  .hoverVisible {
    visibility: hidden;
  }
  &:hover {
    .hoverVisible {
      visibility: visible;
    }
  }
`;

const MenuContent = styled.div<{ layout: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ layout }) => (layout === 'right' ? 'flex-end' : 'flex-start')};
`;

const CombineWrapper = styled.div`
  display: flex;
  > div {
    border-radius: 4px;
    margin-right: 4px;
  }
  > div > div {
    padding: 0;
  }
`;

export default FacebookImageMsg;
